import type { ListBuilderConfiguration } from '../types';

/**
 * System Sent Emails list configuration
 * Shows sent email records with status tracking
 * Features status color rules and prefix/suffix display
 * Converted from JSON to TypeScript for type safety
 */
export const SYSTEM_SENT_EMAILS_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-system-sent-emails',
			cacheTTL: 600000,
			name: 'All Sent Emails',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'email-id',
					headers: [{ id: 'email-id-header', text: 'ID' }],
					fields: [
						{
							id: 'id-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'id', objectTable: 'email_record' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							prefix: '#',
							link: {
								type: 'object',
								enabled: true,
							},
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'email_record',
							fieldName: 'id',
							direction: 'DESC',
						},
					],
					width: 100,
				},
				{
					id: 'subject',
					headers: [{ id: 'subject-header', text: 'Subject' }],
					fields: [
						{
							id: 'subject-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'subject', objectTable: 'email_record' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
							link: {
								type: 'object',
								enabled: true,
							},
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'email_record',
							fieldName: 'subject',
							direction: 'ASC',
						},
					],
					width: 300,
				},
				{
					id: 'recipients',
					headers: [{ id: 'recipients-header', text: 'Recipients' }],
					fields: [
						{
							id: 'recipients-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'recipientsDisplay',
									objectTable: 'email_record',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					width: 250,
				},
				{
					id: 'sent-date',
					headers: [{ id: 'sent-date-header', text: 'Sent Date' }],
					fields: [
						{
							id: 'sent-at-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'sentAt', objectTable: 'email_record' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'date',
							dateFormat: 'MM/DD/YYYY HH:mm',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'email_record',
							fieldName: 'sent_at',
							direction: 'DESC',
						},
					],
					width: 150,
				},
				{
					id: 'status',
					headers: [{ id: 'status-header', text: 'Status' }],
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'sendStatus',
									objectTable: 'email_record',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'status',
									rules: [
										{ value: 'sent', color: 'success' },
										{ value: 'failed', color: 'error' },
										{ value: 'bounced', color: 'warning' },
										{ value: 'complained', color: 'error' },
										{ value: 'pending', color: 'muted' },
									],
								},
							},
							displayStyle: 'badge',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'email_record',
							fieldName: 'send_status',
							direction: 'ASC',
						},
					],
					width: 120,
				},
				{
					id: 'template',
					headers: [{ id: 'template-header', text: 'Template' }],
					fields: [
						{
							id: 'template-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'emailTemplate.name',
									objectTable: 'email_record',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
						},
					],
					width: 200,
				},
				{
					id: 'automation',
					headers: [{ id: 'automation-header', text: 'Automation' }],
					fields: [
						{
							id: 'automation-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'automationStepExecutionId',
									objectTable: 'email_record',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'primary' },
							},
							displayStyle: 'text',
							prefix: 'Execution #',
							link: {
								type: 'custom',
								enabled: true,
								urlTemplate:
									'/automation/executions/{automationStepExecution.executionId}',
								condition: {
									field: 'automationStepExecutionId',
									operator: 'exists',
								},
							},
						},
					],
					width: 150,
				},
				{
					id: 'events',
					headers: [{ id: 'events-header', text: 'Events' }],
					fields: [
						{
							id: 'events-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'eventsCount',
									objectTable: 'email_record',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
							suffix: ' events',
						},
					],
					width: 100,
				},
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: [
			'systemEmailSearch',
			'systemEmailStatus',
			'systemEmailSentDate',
			'systemEmailTemplate',
			'systemEmailHasAutomation',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/email/sent/:id',
			objectLabel: 'Email',
			objectTable: 'email_record',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'subject', fieldLabel: 'Subject' },
				{ fieldName: 'sentAt', fieldLabel: 'Sent At' },
				{ fieldName: 'sendStatus', fieldLabel: 'Status' },
				{ fieldName: 'recipientsDisplay', fieldLabel: 'Recipients' },
			],
		},
	],
};
